import { Component, HostListener, OnInit, OnDestroy, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule, Router, NavigationEnd } from "@angular/router";
import { filter, Subscription } from "rxjs";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialogModule } from "@angular/material/dialog";
import { AuthService } from "../services/auth.service";
import { ProfileService } from "../services/profile.service";
import { PermissionService } from "../services/permission.service";
import { StationContextService } from "../services/station-context.service";
import { ThemeService } from "../services/theme.service";
import { NotificationService } from "../services/production-request.service";
import { ConfirmDialogService } from "../services/confirm-dialog.service";
import { Notification } from "../models/production-request.model";
import { isNewUserSignupNotification, isRecordingNotification, isSupportTicketNotification, notificationDeepLinkLabel, supportTicketReferenceId, toPositiveId } from "../utils/notification-deep-link";
import { CartService } from "../services/cart.service";

type Role = "admin" | "owner" | "station_owner" | "station_user" | "user" | undefined;

interface NavItem {
  label: string;
  icon: string;
  route: string;
  roles: Role[]; // roles that can see the item; undefined means visible to all
}

@Component({
  selector: "app-layout-shell",
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule, MatTooltipModule, MatDialogModule],
  templateUrl: "./layout-shell.component.html",
  styleUrl: "./layout-shell.component.css",
})
export class LayoutShellComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private profileService = inject(ProfileService);
  private permissionService = inject(PermissionService);
  private stationContext = inject(StationContextService);
  private router = inject(Router);
  private confirmDialog = inject(ConfirmDialogService);
  private notificationService = inject(NotificationService);
  private cartService = inject(CartService);
  readonly theme = inject(ThemeService);

  isSidebarCollapsed = false;
  isMobileMenuOpen = false;
  private _resizeHandler = () => this.onResize();

  /** Bumps when effective or assignment roles change so nav getters re-run. */
  private navRoleRevision = signal(0);
  /** Shown on profile icon hover; from session or cached profile. */
  userEmail = signal("");
  navReady = signal(false);
  unreadCount = signal(0);
  cartCount = signal(0);
  notifications = signal<Notification[]>([]);
  notificationsLoading = signal(false);
  notificationDropdownOpen = signal(false);
  private loadedNotificationIds: number[] = [];
  private readonly snoozedNotificationsStorageKey = "rad-snoozed-notifications";
  private snoozedNotificationIds = new Map<number, number>();
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  private closeDrawerOnNavSub?: Subscription;
  private emailSub?: Subscription;
  private profileReadySub?: Subscription;
  private roleSub?: Subscription;
  private cartCountSub?: Subscription;

  navItems: NavItem[] = [
    { label: "Home", icon: "home", route: "/userhome", roles: ["admin", "owner", "station_owner", "station_user", "user"] },
    { label: "Orders", icon: "receipt_long", route: "/orders", roles: ["admin", "owner", "station_owner", "station_user", "user"] },
    { label: "Commercial Requests", icon: "campaign", route: "/production-requests", roles: ["admin", "owner", "station_owner", "station_user", "user"] },
    { label: "Recordings", icon: "mic", route: "/recordings", roles: ["admin", "owner", "station_owner", "station_user", "user"] },
    { label: "Stations", icon: "radio", route: "/managestations", roles: ["admin", "owner", "station_owner", "station_user", "user"] },
    // Hide for plain user role per requirement
    { label: "Station Clocks", icon: "schedule", route: "/managestationclocks", roles: ["admin", "owner", "station_owner", "station_user"] },
    { label: "Ratings", icon: "trending_up", route: "/manageratings", roles: ["admin", "owner", "station_owner", "station_user"] },
    // Hide for plain user role per requirement
    { label: "Station Rates", icon: "attach_money", route: "/managestationrates", roles: ["admin", "owner", "station_owner", "station_user"] },
    // Billing is owner/admin data (backend BILLING_ROLES, checked per station).
    { label: "Billing & Analytics", icon: "bar_chart", route: "/billing", roles: ["admin", "owner"] },
    { label: "Owners", icon: "person_outline", route: "/manageowners", roles: ["admin"] },
    { label: "Staff Directory", icon: "group", route: "/staff-directory", roles: ["admin"] },
    { label: "User Analytics", icon: "insights", route: "/user-analytics", roles: ["admin"] },
    { label: "My Offers", icon: "card_giftcard", route: "/my-offers", roles: ["user"] },
    { label: "Help", icon: "help", route: "/help", roles: ["user"] },
    { label: "Raise a Ticket", icon: "help", route: "/help", roles: ["owner", "station_owner", "station_user"] },
    { label: "Support Inbox", icon: "inbox", route: "/help/tickets", roles: ["admin", "owner", "station_owner", "station_user"] },
  ];

  get visibleItems(): NavItem[] {
    void this.navRoleRevision();
    const assignmentRoles = this.stationContext.getAssignments().map((a) => a.station_role);
    return this.navItems.filter((i) => {
      if (i.route === "/my-offers") {
        return this.permissionService.isAdvertiserUserOnly();
      }
      const role = (this.permissionService.getBaseRole() || "").toLowerCase();
      // Advertisers use Help; station staff use Raise a Ticket; admin has neither. Decided
      // from the CURRENT role and station assignments (users.role can still read "user"
      // after an advertiser is assigned to a station).
      if (i.label === "Help") {
        return role !== "admin" && !this.permissionService.isStationStaffAccount();
      }
      if (i.label === "Raise a Ticket") {
        return this.permissionService.isStationStaffAccount();
      }
      return (
        !i.roles ||
        this.permissionService.meetsRouteRoleRequirement(
          i.roles.filter((r): r is NonNullable<Role> => r != null),
          assignmentRoles,
        )
      );
    });
  }

  /** Promo Codes management is exposed via a dedicated topbar action for admin/owner. */
  get canManagePromoCodes(): boolean {
    void this.navRoleRevision();
    const assignmentRoles = this.stationContext.getAssignments().map((a) => a.station_role);
    return this.permissionService.meetsRouteRoleRequirement(["admin", "owner"], assignmentRoles);
  }

  /**
   * Staff vs advertiser support destination: /help/tickets or /help.
   *
   * Uses PermissionService.isStationStaffAccount(), the same source visibleItems()
   * uses to decide whether this account sees "Help" or "Raise a Ticket". The two
   * must agree. It reflects the CURRENT role and station assignments, so an
   * advertiser who is later assigned to a station moves to the staff surface.
   *
   * This is deliberately an account-level question, not a station-scoped one:
   * which support surface a person belongs to does not change when they switch
   * station. Station-scoped permissions continue to resolve through
   * meetsRouteRoleRequirement() with the per-station assignment roles above.
   *
   * void navRoleRevision() keeps the getter re-evaluating when currentRole$
   * emits (login, profile load, station switch), so the value is never stale.
   */
  get isStaffNav(): boolean {
    void this.navRoleRevision();
    return this.permissionService.getBaseRole() === "admin" || this.permissionService.isStationStaffAccount();
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  openMobileMenu(): void {
    this.isMobileMenuOpen = true;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  go(route: string) {
    this.router.navigateByUrl(route);
  }

  private refreshUserEmail(): void {
    const fromAuth = this.auth.getCurrentUserInfo()?.email as string | undefined;
    const fromProfile = this.profileService.getCurrentUser()?.email;
    const email = (fromAuth || fromProfile || "").trim();
    this.userEmail.set(email);
  }

  confirmLogout() {
    this.confirmDialog
      .open({
        title: "Log out",
        message: "Are you sure you want to log out?",
        confirmLabel: "Logout",
        cancelLabel: "Cancel",
        confirmIcon: "logout",
      })
      .subscribe((ok) => {
        if (ok) {
          setTimeout(() => {
            this.auth.logout();
            this.router.navigate(["/login"]);
          }, 150);
        }
      });
  }

  onResize(): void {
    if (window.innerWidth < 1280) {
      this.isSidebarCollapsed = true;
    } else {
      this.isSidebarCollapsed = false;
    }

    if (window.innerWidth >= 768) {
      this.closeMobileMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseDrawer(): void {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.notificationDropdownOpen() && target && !target.closest('.notification-bell-wrap')) {
      this.closeNotificationDropdown();
    }
  }

  ngOnInit(): void {
    this.onResize();
    window.addEventListener("resize", this._resizeHandler);
    this.loadSnoozedNotifications();
    this.closeDrawerOnNavSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMobileMenuOpen) {
          this.closeMobileMenu();
        }
      });
    this.refreshUserEmail();
    this.emailSub = this.profileService.currentUser$.subscribe(() => this.refreshUserEmail());

    this.roleSub = this.permissionService.currentRole$.subscribe(() => {
      this.navRoleRevision.update((n) => n + 1);
    });

    this.profileReadySub = this.profileService.profileReady$.subscribe((ready) => {
      this.navReady.set(ready);
      if (ready) {
        this.navRoleRevision.update((n) => n + 1);
      }
    });

    this.cartCountSub = this.cartService.itemCount$.subscribe((count) => this.cartCount.set(count));

    if (this.auth.isLoggedIn()) {
      this.cartService.refreshCount();
      this.navReady.set(false);
      const email = (this.auth.getCurrentUserInfo()?.email as string | undefined)?.trim();
      if (email) {
        this.profileService.ensureProfileLoaded(email).subscribe();
      } else {
        this.navReady.set(true);
      }
      this.fetchUnreadCount();
      this.pollInterval = setInterval(() => this.fetchUnreadCount(), 30000);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener("resize", this._resizeHandler);
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.closeDrawerOnNavSub?.unsubscribe();
    this.emailSub?.unsubscribe();
    this.profileReadySub?.unsubscribe();
    this.roleSub?.unsubscribe();
    this.cartCountSub?.unsubscribe();
  }

  onLogoError(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (!image.src.endsWith("/assets/favicon.png")) {
      image.src = "assets/favicon.png";
    }
  }

  fetchUnreadCount(): void {
    if (!this.auth.isLoggedIn()) return;
    this.notificationService.unreadCount().subscribe({
      next: (res) => {
        this.clearExpiredSnoozes();
        this.unreadCount.set(Math.max(0, (res.count ?? 0) - this.snoozedNotificationIds.size));
      },
      error: () => {},
    });
  }

  toggleNotificationDropdown(): void {
    const open = !this.notificationDropdownOpen();
    if (open) {
      this.notificationDropdownOpen.set(true);
      this.notificationsLoading.set(true);
      this.loadedNotificationIds = [];
      this.notificationService.list(20).subscribe({
        next: (list) => {
          const items = this.filterSnoozedNotifications(list ?? []);
          this.notifications.set(items);
          this.loadedNotificationIds = items.map((n) => n.id).filter((id): id is number => id != null);
          this.notificationsLoading.set(false);
        },
        error: () => {
          this.notifications.set([]);
          this.notificationsLoading.set(false);
        },
      });
    } else {
      this.closeNotificationDropdown();
    }
  }

  markAllNotificationsRead(): void {
    const unreadIds = this.notifications()
      .filter((notification) => !notification.is_read)
      .map((notification) => notification.id);

    if (unreadIds.length === 0) return;

    this.notificationService.markReadBulk(unreadIds).subscribe({
      next: () => {
        this.notifications.update((items) => items.map((item) => ({ ...item, is_read: 1 })));
        this.fetchUnreadCount();
      },
      error: () => {},
    });
  }

  closeNotificationDropdown(): void {
    this.notificationDropdownOpen.set(false);
  }

  onNotificationClick(n: Notification): void {
    if (!n.is_read) {
      this.notificationService.markRead(n.id).subscribe({
        next: () => this.fetchUnreadCount(),
        error: () => {},
      });
    }

    if (isSupportTicketNotification(n)) {
      const ticketId = supportTicketReferenceId(n);
      if (ticketId != null) {
        void this.router.navigate(["/help/tickets", ticketId]);
      } else {
        void this.router.navigate([this.isStaffNav ? "/help/tickets" : "/help"]);
      }
    } else if (isNewUserSignupNotification(n)) {
      void this.router.navigate(["/user-analytics"]);
    } else {
      const ref = toPositiveId(n.reference_id);
      if (ref != null) {
        if (isRecordingNotification(n)) {
          void this.router.navigate(["/recordings"], { queryParams: { recordingId: ref } });
        } else {
          void this.router.navigate(["/production-requests", ref]);
        }
      }
    }
    this.closeNotificationDropdown();
  }

  dismissNotification(n: Notification, event: Event): void {
    event.stopPropagation();
    this.notificationService.markRead(n.id).subscribe({
      next: () => {
        this.removeNotificationFromPanel(n.id);
        this.fetchUnreadCount();
      },
      error: () => {},
    });
  }

  snoozeNotification(n: Notification, event: Event): void {
    event.stopPropagation();
    const oneHourFromNow = Date.now() + 60 * 60 * 1000;
    this.snoozedNotificationIds.set(n.id, oneHourFromNow);
    this.persistSnoozedNotifications();
    this.removeNotificationFromPanel(n.id);
    this.unreadCount.update((count) => Math.max(0, count - (n.is_read ? 0 : 1)));
  }

  notificationIcon(n: Notification): string {
    if (isSupportTicketNotification(n)) return "support_agent";
    if (isNewUserSignupNotification(n)) return "person_add";
    if (isRecordingNotification(n)) return "mic_none";
    if (n.type?.toLowerCase().includes("message")) return "chat_bubble_outline";
    return "campaign";
  }

  notificationActionLabel(n: Notification): string {
    return notificationDeepLinkLabel(n);
  }

  timeAgo(created_at: string): string {
    if (!created_at) return "";
    const d = new Date(created_at);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return "Just now";
    if (sec < 3600) return `${Math.floor(sec / 60)} minutes ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)} hours ago`;
    return `${Math.floor(sec / 86400)} days ago`;
  }

  private removeNotificationFromPanel(id: number): void {
    this.notifications.update((items) => items.filter((item) => item.id !== id));
    this.loadedNotificationIds = this.loadedNotificationIds.filter((loadedId) => loadedId !== id);
  }

  private loadSnoozedNotifications(): void {
    try {
      const stored = localStorage.getItem(this.snoozedNotificationsStorageKey);
      const entries = stored ? (JSON.parse(stored) as Record<string, number>) : {};
      this.snoozedNotificationIds = new Map(
        Object.entries(entries).map(([id, expiry]) => [Number(id), Number(expiry)]),
      );
      this.clearExpiredSnoozes();
    } catch {
      this.snoozedNotificationIds.clear();
    }
  }

  private clearExpiredSnoozes(): void {
    const now = Date.now();
    let changed = false;
    for (const [id, expiry] of this.snoozedNotificationIds) {
      if (expiry <= now) {
        this.snoozedNotificationIds.delete(id);
        changed = true;
      }
    }
    if (changed) this.persistSnoozedNotifications();
  }

  private filterSnoozedNotifications(items: Notification[]): Notification[] {
    this.clearExpiredSnoozes();
    return items.filter((item) => !this.snoozedNotificationIds.has(item.id));
  }

  private persistSnoozedNotifications(): void {
    const serialized = Object.fromEntries(this.snoozedNotificationIds);
    localStorage.setItem(this.snoozedNotificationsStorageKey, JSON.stringify(serialized));
  }
}
